import React, { useState } from "react";
import styled, { keyframes, css } from "styled-components";
import { Modal } from "@material-ui/core";
import theme from "../../../assets/theme";
import { useSnackbar } from "notistack";
import useLoader from "../../../hooks/useLoader";
import { CustomInputField } from "../../common/FormInputs";
import {
  PrimaryCTAButton,
  PrimaryOutlinedCloseCTAButton,
} from "../../common/Buttons";
import { getErrorMessage } from "../../../helpers/functions";
import BundleService from "../../../services/BundleService";

export default function EditBundleName({
  editBundle,
  setEditBundle,
  id,
  caseId,
  title,
  setRefreshDetails,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { setLoader } = useLoader();
  const [bundle, setBundle] = useState(title);
  const [error, setError] = useState("");
  const [focused, setFocused] = useState(false);

  const editBundleName = async (value) => {
    try {
      const payload = { bundleName: value };
      setLoader({ state: true, message: "Updating Bundle Name..." });
      const res = await BundleService.editBundleName(payload, caseId, id);
      if (res?.success === false) {
        enqueueSnackbar(res.message, { variant: "error" });
        return;
      }
      if (res) {
        enqueueSnackbar("Bundle updated successfully", { variant: "success" });
      }
      setRefreshDetails(true);
    } catch (error) {
      const message = getErrorMessage(error);
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setLoader({ state: false });
      setEditBundle(false);
    }
  };

  const handleChange = (event) => {
    const name = event.target.value;
    /* eslint-disable no-useless-escape */
    const regexPattern = /^[^\/\\=]+$/;
    if (!name || regexPattern.test(name)) {
      setError("");
    } else {
      setError(
        `Bundle name should not contain slashes ("/") and equal signs ("=")`,
      );
    }
    setBundle(name);
  };

  const hasChanged = bundle && bundle !== title;
  const isValid = bundle && !error;

  return (
    <Modal
      open={editBundle}
      onClose={() => setEditBundle(false)}
      disableRestoreFocus={true}
    >
      <Overlay>
        <ModalCard role="presentation">
          <TopAccent />

          <ModalHeader>
            <IconCircle>
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </IconCircle>
            <HeaderText>
              <ModalTitle>Edit Bundle Name</ModalTitle>
              <ModalSubtitle>
                Update the name for this document bundle
              </ModalSubtitle>
            </HeaderText>
            <CloseBtn onClick={() => setEditBundle(false)} aria-label="Close">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </CloseBtn>
          </ModalHeader>

          <ModalDivider />

          <ModalBody>
            {/* Current name display */}
            <CurrentNameCard>
              <CardTag>Current Name</CardTag>
              <CurrentNameRow>
                <NameIcon>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" />
                  </svg>
                </NameIcon>
                <CurrentValue>{title}</CurrentValue>
              </CurrentNameRow>
            </CurrentNameCard>

            {/* New name field */}
            <FieldGroup>
              <FieldLabel>
                New Bundle Name <Required>*</Required>
              </FieldLabel>
              <InputWrapper focused={focused} hasError={!!error}>
                <InputIcon>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                </InputIcon>
                <CustomInputField
                  variant="outlined"
                  value={bundle}
                  error={!!error}
                  helperText={error}
                  required={true}
                  onChange={(e) => handleChange(e)}
                  onFocus={() => setFocused(true)}
                  onBlur={() => setFocused(false)}
                />
              </InputWrapper>
            </FieldGroup>

            {/* Change preview */}
            {hasChanged && !error && (
              <ChangePreview>
                <ChangeLabel>Rename preview</ChangeLabel>
                <ChangeRow>
                  <ChangeFrom>{title}</ChangeFrom>
                  <ArrowWrapper>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#00838c"
                      strokeWidth="2.5"
                    >
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </ArrowWrapper>
                  <ChangeTo>{bundle}</ChangeTo>
                </ChangeRow>
              </ChangePreview>
            )}
          </ModalBody>

          <ModalFooter>
            <PrimaryOutlinedCloseCTAButton
              style={{ flex: 1 }}
              onClick={() => setEditBundle(false)}
            >
              Cancel
            </PrimaryOutlinedCloseCTAButton>
            <PrimaryCTAButton
              style={{ flex: 1 }}
              disabled={!isValid}
              onClick={() => editBundleName(bundle)}
            >
              Save Changes
            </PrimaryCTAButton>
          </ModalFooter>
        </ModalCard>
      </Overlay>
    </Modal>
  );
}

const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translate(-50%, -46%) scale(0.96); }
  to   { opacity: 1; transform: translate(-50%, -50%) scale(1); }
`;

const previewIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
`;

const ModalCard = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: calc(100% - 24px);
  max-width: 480px;
  background: #ffffff;
  border-radius: 24px;
  box-shadow:
    0 32px 80px rgba(0, 0, 0, 0.2),
    0 8px 24px rgba(0, 131, 140, 0.12);
  animation: ${fadeSlideIn} 0.28s cubic-bezier(0.34, 1.56, 0.64, 1);
  overflow: hidden;

  @media (max-width: 480px) {
    border-radius: 20px 20px 0 0;
    top: auto;
    bottom: 0;
    left: 0;
    transform: none;
    width: 100%;
    max-width: 100%;
  }
`;

const TopAccent = styled.div`
  height: 4px;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 22px 22px 18px;
`;

const IconCircle = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: linear-gradient(135deg, #e6f7f8 0%, #b2e8eb 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #00838c;
`;

const HeaderText = styled.div`
  flex: 1;
  min-width: 0;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-family: ${theme.fonts.primaryFontExtraBold};
  font-size: 18px;
  color: #293461;
  line-height: 1.2;

  @media (max-width: 380px) {
    font-size: 16px;
  }
`;

const ModalSubtitle = styled.p`
  margin: 4px 0 0;
  font-size: 12px;
  color: #64748b;
  font-family: ${theme.fonts.primaryFontRegular};
`;

const CloseBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1.5px solid #e2e8f0;
  background: #f8fafc;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  transition: all 0.2s ease;
  flex-shrink: 0;
  &:hover {
    background: #fee2e2;
    border-color: #fca5a5;
    color: #ef4444;
    transform: rotate(90deg);
  }
`;

const ModalDivider = styled.div`
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    #e2e8f0 30%,
    #e2e8f0 70%,
    transparent
  );
  margin: 0 22px;
`;

const ModalBody = styled.div`
  padding: 22px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CurrentNameCard = styled.div`
  padding: 14px 16px;
  background: #f8fafc;
  border-radius: 14px;
  border: 1.5px solid #e2e8f0;
`;

const CardTag = styled.p`
  margin: 0 0 8px;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #94a3b8;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const CurrentNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const NameIcon = styled.div`
  color: #94a3b8;
  display: flex;
  flex-shrink: 0;
`;

const CurrentValue = styled.p`
  margin: 0;
  font-size: 14px;
  color: #475569;
  font-family: ${theme.fonts.primaryFontSemiBold};
  word-break: break-word;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FieldLabel = styled.label`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 13px;
  color: #334155;
  display: flex;
  align-items: center;
  gap: 2px;
`;

const Required = styled.span`
  color: #ef4444;
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  .MuiOutlinedInput-root {
    padding-left: 36px !important;
    border-radius: 12px !important;
    transition: all 0.2s ease;
    ${({ focused }) =>
      focused &&
      css`
        box-shadow: 0 0 0 3px rgba(0, 131, 140, 0.12);
      `}
  }
`;

const InputIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  z-index: 1;
  pointer-events: none;
  display: flex;
`;

const ChangePreview = styled.div`
  padding: 14px 16px;
  background: linear-gradient(135deg, #f0fdfb 0%, #e6f7f8 100%);
  border: 1.5px solid #99f6e4;
  border-radius: 14px;
  animation: ${previewIn} 0.2s ease;
`;

const ChangeLabel = styled.p`
  margin: 0 0 10px;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #14b8a6;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const ChangeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const ChangeFrom = styled.span`
  font-size: 13px;
  color: #94a3b8;
  text-decoration: line-through;
  font-family: ${theme.fonts.primaryFontRegular};
`;

const ArrowWrapper = styled.div`
  flex-shrink: 0;
  display: flex;
`;

const ChangeTo = styled.span`
  font-size: 13px;
  color: #0f766e;
  font-family: ${theme.fonts.primaryFontSemiBold};
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 12px;
  padding: 0 22px 22px;

  @media (max-width: 380px) {
    flex-direction: column-reverse;
  }
`;
